import { Controller } from "@hotwired/stimulus"

// Studio's character count, hook preview, team selection and publishing intent.
// Inline mentions are handled by studio-mentions on the same form.
const LIMIT = 3000
// What LinkedIn shows in the feed before "...see more": about 210 characters,
// and never more than three lines.
const FOLD_CHARS = 210
const FOLD_LINES = 3
const SUBMIT_LABELS = { queue: "Add to queue", schedule: "Schedule", now: "Post now", draft: "Save draft" }

export default class extends Controller {
    static targets = ["body", "count", "fold", "foldText", "pingSummary", "pingToggle", "pingPicker", "pingItem", "intent", "when", "submit", "destination", "destinationButton", "destinationSummary", "personalAvatar", "pageAvatar", "neutralAvatar"]
    static values = { prompts: Object }

    connect() {
        this.editing = this.submitTarget.textContent.trim() === "Save"
        this.update()
        this.syncAll()
        this.syncDestinations()
    }

    update() {
        const length = this.bodyTarget.value.length
        this.countTarget.textContent = `${length.toLocaleString()} / ${LIMIT.toLocaleString()}`
        this.countTarget.classList.toggle("is-over", length > LIMIT)
        this.updateFold()
        const intent = this.intentTargets.find((input) => input.checked)?.value
        if (this.hasWhenTarget) this.whenTarget.hidden = intent !== "schedule"
        if (!this.editing && intent) this.submitTarget.textContent = SUBMIT_LABELS[intent]
    }

    focusDestinations() {
        if (this.destinationButtonTarget.getAttribute("aria-expanded") === "true") {
            this.destinationTargets[0]?.focus()
        }
    }

    publishingKeydown(event) {
        if (event.key === "Escape") this.destinationButtonTarget.focus()
    }

    syncDestinations() {
        if (!this.hasDestinationSummaryTarget) return
        const selected = this.destinationTargets.filter((input) => input.checked)
        const labels = selected.map((input) => input.dataset.label)
        this.destinationSummaryTarget.textContent = labels.length ? `${labels[0]}${labels.length > 1 ? ` +${labels.length - 1}` : ""}` : "Choose destinations"
        this.destinationButtonTarget.title = labels.join(", ") || "Choose publishing destinations"
        if (!this.hasPageAvatarTarget) return
        const page = selected.some((input) => input.value === "linkedin_page")
        const personal = selected.some((input) => input.value !== "linkedin_page")
        this.pageAvatarTarget.hidden = !page
        this.personalAvatarTarget.hidden = !personal
        this.neutralAvatarTarget.hidden = selected.length > 0
        const identity = page ? (personal ? "both" : "page") : (personal ? (selected.some((input) => input.value === "linkedin") ? "linkedin" : "personal") : "none")
        this.bodyTarget.placeholder = this.promptsValue[identity]
    }

    updateFold() {
        if (!this.hasFoldTarget) return
        const text = this.bodyTarget.value.trim()
        const lines = text.split("\n")
        const folds = text.length > FOLD_CHARS || lines.length > FOLD_LINES
        this.foldTarget.hidden = !folds
        if (folds) this.foldTextTarget.textContent = lines.slice(0, FOLD_LINES).join("\n").slice(0, FOLD_CHARS).trimEnd()
    }

    togglePeople() {
        const open = this.pingPickerTarget.hidden
        this.pingPickerTarget.hidden = !open
        this.pingToggleTarget.setAttribute("aria-expanded", String(open))
        this.pingToggleTarget.textContent = open ? "Done" : "Choose"
        if (open) this.pingPickerTarget.querySelector("input[type=search]")?.focus()
    }

    filterPeople(event) {
        const query = event.target.value.trim().toLowerCase()
        this.pingItemTargets.forEach((input) => {
            const row = input.closest(".studio-person")
            row.hidden = query !== "" && !row.dataset.name.includes(query)
        })
    }

    // All / None act on the people the filter shows, so "filter: sales, All"
    // picks the sales team.
    selectAll() { this.setVisible(true) }
    selectNone() { this.setVisible(false) }

    setVisible(checked) {
        this.pingItemTargets.forEach((input) => {
            if (!input.closest(".studio-person").hidden) input.checked = checked
        })
        this.syncAll()
    }

    syncAll() {
        if (!this.hasPingSummaryTarget) return
        const total = this.pingItemTargets.length
        const picked = this.pingItemTargets.filter((input) => input.checked)
        let text
        if (picked.length === total) text = `Everyone · ${total} ${total === 1 ? "person" : "people"}`
        else if (picked.length === 0) text = "Nobody - no Slack messages"
        else if (picked.length <= 3) text = picked.map((input) => input.closest(".studio-person").querySelector(".studio-person-name").textContent).join(", ")
        else text = `${picked.length} of ${total} people`
        this.pingSummaryTarget.textContent = text
    }
}
